import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  Divider,
  Chip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { Account } from 'axiosApi/models/account';
import intl from 'components/i18n/ReactIntlWrapper';
import { MatchingLedgerEntry } from 'api/InvoiceApiWrapper';

export interface PaymentSeatData {
  idAccount: number;
  description: string;
  amount: number;
  date: Date;
}

interface Props {
  open: boolean;
  invoiceId: number;
  invoiceNumber: string;
  invoiceFrom: string;
  invoiceAmount: number;
  invoiceCurrencyCode: string;
  onConfirm: (data: PaymentSeatData) => Promise<void>;
  onConfirmExisting: (accountingSeatId: number) => Promise<void>;
  onMarkPaidOnly: () => Promise<void>;
  onCancel: () => void;
}

type Step = 'form' | 'matching';

const InvoicePaymentSeatDialog: React.FC<Props> = ({
  open,
  invoiceId,
  invoiceNumber,
  invoiceFrom,
  invoiceAmount,
  invoiceCurrencyCode,
  onConfirm,
  onConfirmExisting,
  onMarkPaidOnly,
  onCancel,
}) => {
  const api: Api = useApi();

  const defaultDescription = `Payment for Invoice #${invoiceNumber ?? ''} - ${invoiceFrom ?? ''} - ${invoiceAmount ?? ''} ${invoiceCurrencyCode ?? ''}`;

  const [account, setAccount] = useState<Account | null>(null);
  const [accountInputValue, setAccountInputValue] = useState('');
  const [accountOptions, setAccountOptions] = useState<Account[]>([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [description, setDescription] = useState(defaultDescription);
  const [amount, setAmount] = useState<string>(invoiceAmount != null ? String(invoiceAmount) : '');
  const [date, setDate] = useState<Date>(new Date());
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ account?: string; amount?: string }>({});

  // Matching flow state
  const [step, setStep] = useState<Step>('form');
  const [matchingEntries, setMatchingEntries] = useState<MatchingLedgerEntry[]>([]);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);

  // Update defaults when invoice props arrive (dialog may open before data is ready)
  useEffect(() => {
    if (open) {
      setDescription(`Payment for Invoice #${invoiceNumber ?? ''} - ${invoiceFrom ?? ''} - ${invoiceAmount ?? ''} ${invoiceCurrencyCode ?? ''}`);
      setAmount(invoiceAmount != null ? String(invoiceAmount) : '');
      setAccount(null);
      setAccountInputValue('');
      setAccountOptions([]);
      setDate(new Date());
      setErrors({});
      setStep('form');
      setMatchingEntries([]);
      setSelectedMatchId(null);
    }
  }, [open, invoiceNumber, invoiceFrom, invoiceAmount, invoiceCurrencyCode]);

  const searchAccounts = useCallback(
    async (term: string) => {
      setAccountLoading(true);
      try {
        const res = await api.invoiceApi.apiVversionAccountSearchGet(
          '1',
          1,
          20,
          term,
          undefined,
          undefined,
          undefined
        );
        setAccountOptions(res.data?.data?.queryResult ?? []);
      } catch {
        setAccountOptions([]);
      } finally {
        setAccountLoading(false);
      }
    },
    [api]
  );

  // Debounce account search
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      searchAccounts(accountInputValue);
    }, 350);
    return () => clearTimeout(timer);
  }, [accountInputValue, open, searchAccounts]);

  const validate = (): boolean => {
    const errs: { account?: string; amount?: string } = {};
    if (!account) errs.account = intl.get('paymentSeat.accountRequired') || 'Account is required';
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) errs.amount = intl.get('paymentSeat.amountRequired') || 'Amount must be greater than 0';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /**
   * Called when user clicks "Register & Continue".
   * Before submitting, checks for existing ledger entries that match the amount and date window.
   * If matches are found, switches to the matching step.
   */
  const handleConfirmClick = async () => {
    if (!validate()) return;

    const amountCents = Math.round(parseFloat(amount) * 100);

    setMatchingLoading(true);
    try {
      const res = await api.invoiceApiWrapper.getMatchingLedgerEntries('1', invoiceId, amountCents, date);
      const entries: MatchingLedgerEntry[] = (res.data as any)?.data ?? [];
      if (entries.length > 0) {
        setMatchingEntries(entries);
        setSelectedMatchId(null);
        setStep('matching');
        return;
      }
    } catch {
      // If the check fails, fall through to normal create flow
    } finally {
      setMatchingLoading(false);
    }

    await submitNew();
  };

  const submitNew = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        idAccount: account!.id!,
        description,
        amount: parseFloat(amount),
        date,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaidOnlyClick = async () => {
    setSubmitting(true);
    try {
      await onMarkPaidOnly();
    } finally {
      setSubmitting(false);
    }
  };

  const handleMatchingConfirm = async () => {
    if (selectedMatchId === null) {
      // User chose "None of these – create new"
      setStep('form');
      await submitNew();
      return;
    }
    setSubmitting(true);
    try {
      await onConfirmExisting(selectedMatchId);
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmount = (cents: number) =>
    `${(cents / 100).toFixed(2)} ${invoiceCurrencyCode ?? ''}`;

  // ── Matching step ──────────────────────────────────────────────────────────
  if (step === 'matching') {
    return (
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {intl.get('paymentSeat.matchingTitle') || 'Possible Existing Movement Found'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Alert severity="info">
              {intl.get('paymentSeat.matchingSubtitle') ||
                'We found ledger movements with the same amount in this month. Is one of them the payment for this invoice?'}
            </Alert>

            <List disablePadding>
              {matchingEntries.map((entry) => (
                <React.Fragment key={entry.id}>
                  <ListItemButton
                    selected={selectedMatchId === entry.id}
                    onClick={() => setSelectedMatchId(entry.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <Radio
                      checked={selectedMatchId === entry.id}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatAmount(entry.amount)}
                          </Typography>
                          <Chip
                            label={new Date(entry.date).toLocaleDateString()}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block">
                            {entry.account?.name ?? ''}{entry.account?.accountNumber ? ` — ${entry.account.accountNumber}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.description}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}

              {/* Option to create new anyway */}
              <ListItemButton
                selected={selectedMatchId === null}
                onClick={() => setSelectedMatchId(null)}
                sx={{ borderRadius: 1, mt: 0.5 }}
              >
                <Radio
                  checked={selectedMatchId === null}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      {intl.get('paymentSeat.matchingNone') || 'None of these — create a new ledger entry'}
                    </Typography>
                  }
                />
              </ListItemButton>
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStep('form')} disabled={submitting} color="inherit">
            {intl.get('button.back') || 'Back'}
          </Button>
          <Button
            onClick={handleMatchingConfirm}
            disabled={submitting}
            variant="contained"
            color="primary"
          >
            {submitting ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : selectedMatchId !== null ? (
              intl.get('paymentSeat.matchingLink') || 'Link & Mark as Paid'
            ) : (
              intl.get('paymentSeat.confirm') || 'Register & Continue'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Form step ──────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {intl.get('paymentSeat.title') || 'Register Payment'}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {intl.get('paymentSeat.subtitle') ||
              'Please fill in the ledger details before marking this invoice as paid.'}
          </Typography>

          {/* Account autocomplete */}
          <Autocomplete<Account>
            options={accountOptions}
            getOptionLabel={(opt) =>
              [opt.name, opt.accountNumber].filter(Boolean).join(' — ')
            }
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={account}
            inputValue={accountInputValue}
            loading={accountLoading}
            onInputChange={(_, val) => setAccountInputValue(val)}
            onChange={(_, val) => {
              setAccount(val);
              if (errors.account) setErrors((e) => ({ ...e, account: undefined }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.get('paymentSeat.account') || 'Account'}
                size="small"
                error={!!errors.account}
                helperText={errors.account}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {accountLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Description */}
          <TextField
            label={intl.get('paymentSeat.description') || 'Description'}
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Amount */}
          <TextField
            label={`${intl.get('paymentSeat.amount') || 'Amount'} ${invoiceCurrencyCode ?? ''}`}
            size="small"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              if (errors.amount) setErrors((errs) => ({ ...errs, amount: undefined }));
            }}
            error={!!errors.amount}
            helperText={errors.amount}
            inputProps={{ min: 0, step: 0.01 }}
          />

          {/* Date */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={intl.get('paymentSeat.date') || 'Payment Date'}
              value={date}
              onChange={(val) => val && setDate(val)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting || matchingLoading} color="inherit">
          {intl.get('button.cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleMarkPaidOnlyClick}
          disabled={submitting || matchingLoading}
          variant="outlined"
          color="secondary"
        >
          {intl.get('paymentSeat.markPaidOnly') || 'Mark as Paid (reconcile later)'}
        </Button>
        <Button
          onClick={handleConfirmClick}
          disabled={submitting || matchingLoading}
          variant="contained"
          color="primary"
        >
          {submitting || matchingLoading ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            intl.get('paymentSeat.confirm') || 'Register & Continue'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaymentSeatDialog;
